import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import { useSidebarStore } from "@/stores/useSidebarStore";
import { useEffect, type JSX } from "react";
import { BsCurrencyEuro } from "react-icons/bs";
import { MdDiscount, MdSubscriptions } from "react-icons/md";
import {
  RiAdminFill,
  RiBarChartFill,
  RiBuilding4Line,
  RiCalendarEventFill,
  RiGroupFill,
  RiMapPin2Fill,
  RiMenu3Fill,
  RiNotification3Fill,
  RiServiceFill,
  RiSettings3Fill,
  RiUserAddFill,
  RiGiftFill,
  RiHistoryFill,
} from "react-icons/ri";
import {
  FileCog,
  List,
  ReceiptText,
  Blocks,
  Wallet,
  Package,
  Truck,
  PackageOpen,
  Scroll,
  BookOpenCheck,
  Store,
  OctagonAlert,
  TriangleAlert,
  Timer,
  CalendarSync,
  UserRoundGroup,
  Bot,
} from "lucide-react";
import useAccountInfo from "../../hooks/useAccountInfo";
import { useScheduleStore } from "../../pages/Dashboard/Schedule/Stores/useScheduleStore";
import useUserPermissionsStore from "../../stores/useUserPermissionsStore";
import { useMediaQuery } from "@mantine/hooks";
import { useLocation } from "react-router";
import { cn } from "@/utils";
import { Chip, Link, LinkProps } from "@heroui/react";
import { useAnalyticsSurface } from "@/hooks/useAnalyticsSurface";
import { useDashboardSurfaces } from "@/hooks/useDashboardSurfaces";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useIntegrationAccess } from "@/hooks/useIntegrationAccess";
import { analyticsSurfaceLink } from "@/utils/analyticsSurface";
import { CALENDAR_LINK } from "@/utils/dashboardSurfaces";
import { isAnyLocationExcludedFromOnlinePayments } from "@/lib/onlinePayments";
import { BottomFixedItems } from "./BottomFixedItems";
import { useSidebarNavigate } from "./useSidebarNavigate";
import "./sidebarShimmer.css";
import { useShallow } from "zustand/react/shallow";
import { canViewChatBooking } from "@/pages/Dashboard/ChatBooking/chatBookingAccess";

type SidebarPageListItem = {
  name: string;
  icon: JSX.Element;
  link: string;
  show: boolean;
  checkLinkStartsWith?: boolean;
  disabled?: boolean;
  status?: "loading" | "warning" | "error";
  highlight?: boolean;
  badge?: string;
};

const Sidebar = () => {
  const location = useLocation();
  const navigateFromSidebar = useSidebarNavigate();
  const page = location.pathname;

  const { isOpen: sidebarOpen, toggle: setSidebarOpen } = useSidebarStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      toggle: state.toggle,
    })),
  );

  const { selectSchedule } = useScheduleStore(
    useShallow((state) => ({
      selectSchedule: state.selectSchedule,
    })),
  );

  const { isFeatureEnabled } = useClientFeature();

  const analyticsSurface = useAnalyticsSurface();

  const dashboardSurfaces = useDashboardSurfaces();

  const { hasAnyIntegration } = useIntegrationAccess();

  const accountInfo = useAccountInfo((state) => state.accountInfo);

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const { data: stripeAccountData, isPending: isPendingStripeAccountData } =
    api.stripe.useGetStripeAccount(
      isFeatureEnabled("payments") && !!permissions.view_online_payments_page,
    );

  const { data: locationsResponse } = api.location.useGetLocations({
    sortBy: "labelAsc",
  });

  const isAnyLocationInExcludedCountriesForOnlinePayments =
    isAnyLocationExcludedFromOnlinePayments(locationsResponse?.locations);

  const mainPagesList: SidebarPageListItem[] = [
    {
      name: t`Koledar`,
      icon: <RiCalendarEventFill className="shrink-0" size={18} />,
      link: CALENDAR_LINK,
      show: dashboardSurfaces.showCalendar,
    },
    {
      name: t`Pregled strank`,
      icon: <RiGroupFill className="shrink-0" size={18} />,
      link: "/dashboard/customers",
      show: permissions.view_customer_list,
    },
    {
      name: t`Čakalna vrsta`,
      icon: <Timer size={18} />,
      link: "/dashboard/waitlist",
      show: isFeatureEnabled("waitlist") && permissions.view_waitlist,
    },
    {
      name: t`AI asistent`,
      icon: <Bot className="shrink-0" size={18} />,
      link: "/dashboard/chat-booking",
      show: canViewChatBooking({ isFeatureEnabled, permissions }),
      checkLinkStartsWith: true,
      badge: t`Beta`,
    },
    {
      name: t`Redni termini`,
      icon: <CalendarSync className="shrink-0" size={18} />,
      link: "/dashboard/fixed-appointments",
      show: isFeatureEnabled("fixedAppointments"),
    },
    {
      name: t`Analitika`,
      icon: <RiBarChartFill className="shrink-0" size={18} />,
      link: analyticsSurfaceLink(analyticsSurface),
      show: dashboardSurfaces.showAnalytics,
      checkLinkStartsWith: analyticsSurface === "v2",
    },
    {
      name: t`Skupnost`,
      icon: <UserRoundGroup className="shrink-0" size={18} />,
      link: "/dashboard/community",
      show: true,
      highlight: true,
    },
    {
      name: t`Godmode`,
      icon: <RiAdminFill className="shrink-0" size={18} />,
      link: "/dashboard/admin",
      show: ["admin", "salesman"].includes(accountInfo.systemRole),
      checkLinkStartsWith: true,
    },
  ];

  const taxRegisterPagesList: SidebarPageListItem[] = isFeatureEnabled(
    "taxRegister",
  )
    ? [
        {
          name: t`Nov račun`,
          icon: <ReceiptText className="shrink-0" size={18} />,
          link: "/dashboard/tax-register/invoice/new",
          show: permissions.view_pos_page,
        },
        {
          name: t`Arhiv računov`,
          icon: <List className="shrink-0" size={18} />,
          link: "/dashboard/tax-register/invoice",
          show: permissions.view_invoice_archive_page,
        },
        {
          name: t`Blagajna`,
          icon: <Wallet className="shrink-0" size={18} />,
          link: "/dashboard/tax-register/cash-register",
          show:
            permissions.view_cash_register_closures ||
            permissions.view_cash_register_status_summary_view,
          checkLinkStartsWith: true,
        },
        {
          name: t`Zaloga`,
          icon: <PackageOpen className="shrink-0" size={18} />,
          link: "/dashboard/products/stock",
          show:
            isFeatureEnabled("products") && permissions.view_product_stock_page,
          checkLinkStartsWith: true,
        },
        {
          name: t`Inventura`,
          icon: <Scroll className="shrink-0" size={18} />,
          link: "/dashboard/stock-taking",
          show:
            isFeatureEnabled("products") && permissions.view_stock_taking_page,
          checkLinkStartsWith: true,
        },
        {
          name: t`Dobavnice`,
          icon: <Truck className="shrink-0" size={18} />,
          link: "/dashboard/delivery-orders",
          show:
            isFeatureEnabled("products") &&
            permissions.view_delivery_orders_page,
          checkLinkStartsWith: true,
        },
        {
          name: t`Nastavitve`,
          icon: <FileCog className="shrink-0" size={18} />,
          link: "/dashboard/tax-register/settings/setup",
          show: permissions.view_tax_register_settings_page,
          checkLinkStartsWith: true,
        },
      ]
    : [];

  const settingsPagesList: SidebarPageListItem[] = [
    {
      name: t`Platforma`,
      icon: <Store className="shrink-0" size={18} />,
      link: "/dashboard/marketplace/settings",
      show: isFeatureEnabled("marketplace") && permissions.view_locations_page,
      checkLinkStartsWith: true,
    },
    {
      name: t`Lokacija`,
      icon: <RiMapPin2Fill className="shrink-0" size={18} />,
      link: "/dashboard/locations",
      show: permissions.view_locations_page,
    },
    {
      name: t`Paketi`,
      icon: <MdSubscriptions className="shrink-0" size={18} />,
      link: "/dashboard/subscriptions",
      show:
        isFeatureEnabled("subscriptions") &&
        permissions.view_subscriptions_page,
    },
    {
      name: t`Pregled paketov strank`,
      icon: <BookOpenCheck className="shrink-0" size={18} />,
      link: "/dashboard/customer-subscriptions-overview",
      show:
        isFeatureEnabled("subscriptions") &&
        permissions.view_customer_subscriptions_overview_page,
    },
    {
      name: t`Storitve`,
      icon: <RiServiceFill className="shrink-0" size={18} />,
      link: "/dashboard/services",
      show: permissions.view_services_page,
    },
    {
      name: t`Kode za popust`,
      icon: <MdDiscount className="shrink-0" size={18} />,
      link: "/dashboard/discount-codes",
      show:
        isFeatureEnabled("discountCodes") &&
        permissions.view_discount_codes_page,
      checkLinkStartsWith: true,
    },
    {
      name: t`Produkti`,
      icon: <Package className="shrink-0" size={18} />,
      link: "/dashboard/products",
      show: isFeatureEnabled("products") && permissions.view_products_page,
      checkLinkStartsWith: false,
    },
    {
      name: t`Sredstva`,
      icon: <RiBuilding4Line className="shrink-0" size={18} />,
      link: "/dashboard/resource-groups",
      show: isFeatureEnabled("resources") && permissions.view_resources_page,
    },
    {
      name: t`Zaposleni`,
      icon: <RiUserAddFill className="shrink-0" size={18} />,
      link: "/dashboard/users",
      show: permissions.view_users_page,
    },

    {
      name: t`Urniki`,
      icon: <RiCalendarEventFill className="shrink-0" size={18} />,
      link: "/dashboard/schedule",
      show: permissions.view_availability,
    },
    {
      name: t`Splošne nastavitve`,
      icon: <RiSettings3Fill className="shrink-0" size={18} />,
      link: "/dashboard/settings",
      show: permissions.view_settings_page,
    },
    {
      name: t`Obvestila`,
      icon: <RiNotification3Fill className="shrink-0" size={18} />,
      link: "/dashboard/notifications",
      show: permissions.view_notifications_page && accountInfo?.onboardingDone,
      checkLinkStartsWith: true,
    },
    {
      name: t`Zgodovina sprememb`,
      icon: <RiHistoryFill className="shrink-0" size={18} />,
      link: "/dashboard/change-log",
      show: isFeatureEnabled("changeLog") && permissions.view_change_log_page,
    },
    {
      name: t`Spletna plačila`,
      icon: <BsCurrencyEuro className="shrink-0" size={18} />,
      link: "/dashboard/stripe",
      checkLinkStartsWith: true,
      show:
        isFeatureEnabled("payments") &&
        permissions.view_online_payments_page &&
        !!locationsResponse &&
        !isAnyLocationInExcludedCountriesForOnlinePayments,
      status: (() => {
        if (isPendingStripeAccountData) {
          return "loading";
        }

        // No warning/error until the client has actually started Stripe setup.
        if (!stripeAccountData?.stripeAccountId) {
          return undefined;
        }

        if (stripeAccountData.requirements?.isDisabled) {
          return "error";
        }

        if (stripeAccountData.requirements?.hasDueRequirements) {
          return "warning";
        }

        return undefined;
      })(),
    },
    {
      name: t`Darilni boni`,
      icon: <RiGiftFill className="shrink-0" size={18} />,
      link: "/dashboard/gift-card",
      show: isFeatureEnabled("giftCard") && permissions.view_gift_cards_page,
    },
    {
      name: t`Integracije`,
      icon: <Blocks className="shrink-0" size={18} />,
      link: "/dashboard/integrations",
      checkLinkStartsWith: true,
      show: hasAnyIntegration && permissions.view_integrations_page,
    },
  ];

  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  // On mobile onSelect navigates itself, after the sidebar has closed; leaving
  // the href on would navigate immediately and skip that.
  const linkHref = (link: string) => (isMobile ? undefined : link);

  const mainPages = mainPagesList.map((item) => {
    if (!item.show) return;

    const isSelected = item.checkLinkStartsWith
      ? page.startsWith(item.link)
      : page === item.link;

    return (
      <SidebarItem
        key={item.link}
        isOpen={sidebarOpen}
        isSelected={isSelected}
        onPress={() => onSelect(item.link)}
        href={linkHref(item.link)}
        icon={item.icon}
        text={item.name}
        highlight={item.highlight}
        badge={item.badge}
      />
    );
  });

  const taxRegisterPages = taxRegisterPagesList.map((item) => {
    if (!item.show) return;

    const isSelected = item.checkLinkStartsWith
      ? page.startsWith(item.link)
      : page === item.link;

    return (
      <SidebarItem
        key={item.link}
        isOpen={sidebarOpen}
        isDisabled={item.disabled}
        isSelected={isSelected}
        onPress={item.disabled ? undefined : () => onSelect(item.link)}
        href={item.disabled ? undefined : linkHref(item.link)}
        icon={item.icon}
        text={item.name}
      />
    );
  });

  const settingsPages = settingsPagesList.map((item) => {
    if (!item.show) return;

    const isSelected = item.checkLinkStartsWith
      ? page.startsWith(item.link)
      : page === item.link;

    return (
      <SidebarItem
        key={item.link}
        isOpen={sidebarOpen}
        isSelected={isSelected}
        onPress={() => onSelect(item.link)}
        href={linkHref(item.link)}
        icon={item.icon}
        text={item.name}
        status={item.status}
      />
    );
  });

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile, setSidebarOpen]);

  useEffect(() => {
    if (!isMobile || !sidebarOpen) return;

    document.body.classList.add("sidebar-scroll-lock");

    return () => {
      document.body.classList.remove("sidebar-scroll-lock");
    };
  }, [isMobile, sidebarOpen]);

  function onSelect(link: string) {
    if (link === "/dashboard/schedule") {
      selectSchedule(null);
    }

    // Desktop items navigate through their href; only mobile needs the close
    // and the navigation that follows it.
    if (!isMobile) return;

    navigateFromSidebar(link);
  }

  return (
    <>
      <div
        className={cn(
          "ease-lime-bezier-ease transition-width duration-300",
          // Pinned to the viewport rather than the safe-area-padded body box,
          // so the panel also covers the status bar strip instead of letting
          // page content show through above it.
          "fixed top-0 left-0 z-50 flex h-screen max-h-screen flex-col overflow-scroll overflow-x-hidden bg-slate-100 pt-[calc(1.75rem_+_var(--safe-area-top,0px))] pb-[100px] text-left [scrollbar-width:none] dark:bg-neutral-900",
          "md:relative md:z-auto md:h-auto md:bg-transparent md:px-0 md:pt-7 md:pb-0",
          {
            "w-0 md:w-[60px]": !sidebarOpen,
            "w-full md:w-[270px]": sidebarOpen,
          },
        )}
      >
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={cn(
            "ease-lime-bezier-ease transition-transform duration-300",
            "mb-8 flex -translate-x-[122px] cursor-pointer items-center justify-between border-none bg-none px-5",
            {
              "translate-x-0": sidebarOpen,
            },
          )}
        >
          <img
            className={cn("ease-lime-bezier-ease transition-all duration-200", {
              "opacity-100": sidebarOpen,
              "opacity-0": !sidebarOpen,
            })}
            src="/logo_full_green.svg"
            alt="Lime"
            style={{ userSelect: "none", maxWidth: "120px" }}
          />
          <div>
            <RiMenu3Fill style={{ fontSize: "25px", color: "#A0C5B5" }} />
          </div>
        </button>
        <ItemsWrapper>{mainPages}</ItemsWrapper>

        {isFeatureEnabled("taxRegister") && (
          <>
            <PagesGroupLabel isOpen={sidebarOpen}>
              <Trans>Davčna blagajna</Trans>
            </PagesGroupLabel>
            <ItemsWrapper>{taxRegisterPages}</ItemsWrapper>
          </>
        )}

        <PagesGroupLabel isOpen={sidebarOpen}>
          <Trans>Nastavitve</Trans>
        </PagesGroupLabel>
        <ItemsWrapper>{settingsPages}</ItemsWrapper>

        <div style={{ flex: 1, padding: "90px 0" }}></div>

        <BottomFixedItems />
      </div>
    </>
  );
};

const ItemsWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col gap-1">{children}</div>;
};

const PagesGroupLabel = ({
  children,
  isOpen,
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) => {
  return (
    <p
      className={cn(
        "ease-lime-bezier-ease mt-6 h-7 pl-5 text-xs font-medium whitespace-nowrap text-[#969ba9] opacity-0 transition-all duration-200",
        {
          "opacity-100": isOpen,
        },
      )}
    >
      {children}
    </p>
  );
};

const SidebarItem = ({
  isSelected,
  isOpen,
  isDisabled,
  text,
  icon,
  status,
  highlight,
  badge,
  ...props
}: LinkProps & {
  isSelected?: boolean;
  isOpen?: boolean;
  isDisabled?: boolean;
  text: string;
  icon: React.ReactNode;
  status?: SidebarPageListItem["status"];
  highlight?: boolean;
  badge?: string;
}) => {
  const statusIcon = (() => {
    if (status === "loading") {
      return null;
    }

    if (status === "warning") {
      return <TriangleAlert size={16} className="shrink-0" />;
    }

    if (status === "error") {
      return <OctagonAlert size={16} className="shrink-0" />;
    }

    return null;
  })();

  return (
    <Link
      {...props}
      className={cn(
        "ease-[cubic-bezier(0.19, 1, 0.22, 1)] transition-all duration-500",
        "flex h-9 min-w-36 cursor-pointer items-center justify-start",
        "hover:text-white",
        {
          "pointer-events-none": isDisabled,
        },
      )}
    >
      {/** left knob */}
      <div
        className={cn(
          "ease-[cubic-bezier(0.19, 1, 0.22, 1)] transition-all duration-200",
          "bg-primary relative h-full w-0 rounded-r-sm",
          {
            "w-1": isSelected,
            "rounded-none": status === "warning" || status === "error",
          },
        )}
      ></div>

      {/** content */}
      <div
        className={cn(
          "ease-[cubic-bezier(0.4, 0, 0.2, 1)] transition-background duration-150",
          "ml-[10px] flex h-full w-full items-center rounded-l-md bg-transparent pl-[10px] font-medium whitespace-pre text-(--secondary-text)",
          {
            "bg-primary overflow-hidden text-white": isSelected,
            "hover:bg-[#dfece6] dark:hover:bg-neutral-800": !isSelected,
            "bg-warning-500 ml-0 rounded-none text-white": status === "warning",
            "bg-danger-500 ml-0 rounded-none text-white": status === "error",
            "animate-pulse duration-1000": status === "loading",
            "sidebar-shimmer": highlight && !isSelected,
          },
        )}
      >
        <div
          className={cn("flex h-[20px] w-[20px] items-center", {
            "mr-3": status === "warning" || status === "error",
          })}
        >
          {statusIcon} {icon}
        </div>
        <p
          className={cn(
            "ease-[cubic-bezier(0.19, 1, 0.22, 1)] transition-all duration-200",
            "z-10 translate-x-1 pl-[10px] text-sm opacity-0",
            {
              "translate-x-0 opacity-100": isOpen,
            },
          )}
        >
          {text}
        </p>
        {badge && isOpen ? (
          <Chip
            size="sm"
            variant="flat"
            color="primary"
            className={cn("ml-2 h-5 shrink-0 text-xs", {
              "bg-white/20 text-white": isSelected,
            })}
          >
            {badge}
          </Chip>
        ) : null}
      </div>
    </Link>
  );
};

export default Sidebar;
